import { isDefined, toError } from "@clipboard-health/util-ts";
import { type Agreement, type SignedAgreement } from "@src/appV2/Agreements/types";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { logError } from "@src/appV2/lib/analytics/log";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { ICAgreementFormV2 } from "@src/appV2/redesign/ICAgreement/ICAgreementFormV2";
import { type Worker } from "@src/appV2/Worker/api/types";
import { USER_EVENTS } from "@src/constants";

import { OnboardingTitle } from "../../SharedTitle";
import { ONBOARDING_STAGES } from "../../util/constants";
import { useSignICAgreement } from "./useSignICAgreement";

interface ICAgreementStepContainerProps {
  worker: Worker;
  agreementToSign: Agreement | SignedAgreement;
  agreementsStatus: "loading" | "error" | "success";
  onAcceptAgreement: () => Promise<void>;
}

export function ICAgreementStepContainer(props: ICAgreementStepContainerProps) {
  const { worker, agreementToSign, agreementsStatus, onAcceptAgreement } = props;

  useLogEffect(
    APP_V2_USER_EVENTS.SIGN_AGREEMENT_VIEWED,
    {
      hcpId: worker.userId,
      name: worker.name,
      agreementVersion: agreementToSign?.attributes.version,
    },
    { enabled: isDefined(agreementToSign) }
  );

  const { mutateAsync: signICAgreement, isLoading: isSigning } = useSignICAgreement({
    onSuccess: async () => {
      logEvent(USER_EVENTS.SIGNED_CONTRACT, {
        // Keep attributes same as in the old event to avoid breaking any old analytics code
        hcpID: worker.userId,
        email: worker.email,
        signature: worker.name,
        agreementVersion: agreementToSign?.attributes.version,
      });
      logEvent(APP_V2_USER_EVENTS.ONBOARDING_STEP_COMPLETED, {
        stepCompleted: ONBOARDING_STAGES.CONTRACTOR_AGREEMENT,
        signature: worker.name,
        agreementVersion: agreementToSign?.attributes.version,
      });

      await onAcceptAgreement();
    },
    onError: (error) => {
      logError(APP_V2_APP_EVENTS.SIGN_AGREEMENT_FAILURE, {
        error: toError(error),
        metadata: {
          workerId: worker.userId,
          workerName: worker.name,
          agreementVersion: agreementToSign?.attributes.version,
        },
      });
    },
  });

  return (
    <ICAgreementFormV2
      agreementToSign={agreementToSign}
      agreementsStatus={agreementsStatus}
      worker={worker}
      isSigningAgreement={isSigning}
      title={<OnboardingTitle text="Independent Contractor Agreement" />}
      onSignAgreement={async ({ agreementToSign }) => {
        try {
          await signICAgreement({
            agreementVersion: agreementToSign.attributes.version,
            signature: worker.name,
            workerId: worker.userId,
          });
        } catch {
          // Error is already handled by the onError callback in useSignICAgreement
        }
      }}
    />
  );
}
